<template>
  <div class="yt-overview">
    <v-card flat class="yt-hero">
      <div class="yt-hero__left">
        <p class="yt-hero__k">Vues sur la période <span class="yt-src">YouTube Analytics</span></p>
        <p class="yt-hero__v">{{ totals ? fBig(totals.views) : '—' }}</p>
        <p class="yt-hero__x">
          <span>{{ totals ? fInt(totals.views_per_day) : '—' }} vues par jour</span>
          <span v-if="totals">·</span>
          <span v-if="totals">{{ fInt(totals.published) }} vidéo(s) publiée(s)</span>
        </p>
      </div>
      <div class="yt-hero__right">
        <vue-apex-charts v-if="heroSeries.length > 1" type="area" height="120" :options="heroOptions" :series="[{ name: 'Vues', data: heroSeries }]" />
      </div>
    </v-card>

    <div class="yt-split">
      <v-card v-for="split in splits" :key="split.key" flat class="yt-ds">
        <div class="yt-ds__h">
          <h3>{{ split.title }}</h3>
          <span class="yt-src yt-src--calc">Estimation</span>
        </div>
        <div class="yt-ds__figs">
          <div>
            <div class="yt-ds__k"><i class="yt-key" :style="{ background: colors.discover }"></i>Venues de Discover</div>
            <div class="yt-ds__v">{{ split.format(split.discover) }}</div>
            <div class="yt-ds__p">{{ fPct(split.discoverShare) }} du total</div>
          </div>
          <div>
            <div class="yt-ds__k"><i class="yt-key" :style="{ background: split.color }"></i>Hors Discover</div>
            <div class="yt-ds__v">{{ split.format(split.other) }}</div>
            <div class="yt-ds__p">{{ fPct(1 - split.discoverShare) }} du total</div>
          </div>
        </div>
        <div class="yt-sbar">
          <span :style="{ width: `${split.discoverShare * 100}%`, background: colors.discover }"></span>
          <span :style="{ width: `${(1 - split.discoverShare) * 100}%`, background: split.color }"></span>
        </div>
      </v-card>
    </div>

    <div class="yt-kpis">
      <yt-kpi-tile
        v-for="tile in tiles"
        :key="tile.key"
        :label="tile.label"
        :value="tile.value"
        :sub="tile.sub"
        :hint="tile.hint"
        :series="tile.series"
        :color="tile.color"
        :formatter="tile.formatter"
      />
    </div>
    <p class="yt-note">
      Les vues et revenus « venus de Discover » sont estimés : vues = clics Discover de la vidéo ce jour-là (plafonnés à ses vues),
      revenus = ces vues × le RPM de la vidéo ce jour-là. YouTube ne distingue pas Discover dans ses sources de trafic.
    </p>
  </div>
</template>

<script>
import { computed, toRefs } from '@vue/composition-api'
import VueApexCharts from 'vue-apexcharts'
import YtKpiTile from '../YtKpiTile.vue'
import useYoutubePerf from '../useYoutubePerf'
import {
  COLORS, baseChartOptions, bucketLabel, bucketTitle, fBig, fDecimal, fDuration, fEur, fEur2, fHours, fInt, fPct, fPct100,
} from '../ytFormat'

export default {
  name: 'YtOverviewTab',
  components: { VueApexCharts, YtKpiTile },
  props: { active: { type: Boolean, default: false } },
  setup(props) {
    const { active } = toRefs(props)
    const { store, ensureOverview, useTabLoader } = useYoutubePerf()
    useTabLoader(active, ensureOverview)

    const overview = computed(() => store.getters['youtubePerf/getOverview'])
    const totals = computed(() => overview.value?.totals || null)
    const buckets = computed(() => overview.value?.series?.buckets || [])
    const granularity = computed(() => overview.value?.series?.granularity || 'day')
    const labels = computed(() => buckets.value.map((b) => bucketLabel(b, granularity.value)))
    const seriesOf = (key) => buckets.value.map((b) => Number(b[key] || 0))

    const heroSeries = computed(() => seriesOf('views'))
    const heroOptions = computed(() => ({
      ...baseChartOptions(),
      chart: { type: 'area', sparkline: { enabled: true }, animations: { enabled: false } },
      colors: [COLORS.views],
      stroke: { width: 2, curve: 'smooth' },
      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 100] } },
      xaxis: { categories: buckets.value.map((b) => bucketTitle(b, granularity.value)) },
      tooltip: { x: { show: true }, y: { formatter: (v) => fInt(v), title: { formatter: () => 'Vues' } }, marker: { show: false } },
    }))

    const splits = computed(() => {
      const t = totals.value
      if (!t) return []
      return [
        { key: 'views', title: 'Vues', discover: t.discover_views, other: t.other_views, discoverShare: t.discover_views_share, format: fInt, color: COLORS.views },
        { key: 'revenue', title: 'Revenus estimés', discover: t.discover_revenue, other: t.other_revenue, discoverShare: t.discover_revenue_share, format: fEur, color: COLORS.revenue },
      ]
    })

    const tiles = computed(() => {
      const t = totals.value
      if (!t) return []
      const tile = (key, label, value, options = {}) => ({
        key, label, value, sub: options.sub || '', hint: options.hint || '', color: options.color || COLORS.views,
        formatter: options.formatter || fInt, series: seriesOf(options.seriesKey || key),
      })
      return [
        tile('published', 'Vidéos publiées', fInt(t.published), { color: COLORS.published }),
        tile('views_per_day', 'Vues moyennes par jour', fInt(t.views_per_day), { hint: 'Vues ÷ nombre de jours de la période' }),
        tile('revenue', 'Revenus estimés', fEur(t.revenue), { color: COLORS.revenue, formatter: fEur }),
        tile('rpm', 'RPM', fEur2(t.rpm), { hint: 'Revenus ÷ vues × 1 000', color: COLORS.rpm, formatter: fEur2 }),
        tile('cpm', 'CPM', fEur2(t.cpm), { hint: 'Revenu brut ÷ impressions publicitaires × 1 000', color: COLORS.cpm, formatter: fEur2 }),
        tile('hours_watched', 'Heures de visionnage', fHours(t.hours_watched), { formatter: fHours }),
        tile('avg_duration', 'Durée moyenne de visionnage', fDuration(t.avg_duration), { formatter: fDuration }),
        tile('avg_percentage', '% moyen de la vidéo vue', fPct100(t.avg_percentage), { formatter: (v) => fPct100(v) }),
        tile('likes', 'Likes', fInt(t.likes)),
        tile('dislikes', 'Dislikes', fInt(t.dislikes)),
        tile('comments', 'Commentaires', fInt(t.comments)),
        tile('shares', 'Partages', fInt(t.shares)),
      ]
    })

    return { totals, heroSeries, heroOptions, splits, tiles, labels, colors: COLORS, fBig, fInt, fEur, fPct, fDecimal }
  },
}
</script>

<style lang="scss" scoped>
.yt-overview {
  display: flex;
  flex-direction: column;
  gap: $rmr-space-lg;
}

.yt-hero {
  border-radius: $rmr-radius-2xl !important;
  padding: $rmr-space-xl;
  box-shadow: $rmr-shadow-card !important;
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: $rmr-space-lg $rmr-space-xl;
  align-items: end;

  @media (max-width: 760px) { grid-template-columns: 1fr; }
}

.yt-hero__k {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: $rmr-fs-base;
  color: $rmr-text-secondary;
  font-weight: 600;
}

.yt-hero__v {
  margin: 8px 0 6px;
  font-size: clamp(40px, 5vw, 56px);
  line-height: 1;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: $rmr-indigo-dark;
}

.yt-hero__x {
  margin: 0;
  display: flex;
  gap: 8px;
  font-size: $rmr-fs-base;
  color: $rmr-text-muted;
}

.yt-hero__right { min-width: 0; }

.yt-src {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: $rmr-fs-xs;
  font-weight: 600;
  color: $rmr-text-muted;

  &::before { content: ''; width: 7px; height: 7px; border-radius: 50%; background: $rmr-ca; }
  &--calc::before { background: $rmr-text-subtle; }
}

.yt-split {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $rmr-space-md;
  @media (max-width: 760px) { grid-template-columns: 1fr; }
}

.yt-ds {
  border-radius: $rmr-radius-xl !important;
  padding: $rmr-space-md $rmr-space-lg;
  box-shadow: $rmr-shadow-card !important;
  display: flex;
  flex-direction: column;
  gap: $rmr-space-sm;
}

.yt-ds__h {
  display: flex;
  justify-content: space-between;
  align-items: center;
  h3 { margin: 0; font-size: $rmr-fs-title; font-weight: 700; text-transform: uppercase; color: $rmr-indigo-dark; letter-spacing: 0.5px; }
}

.yt-ds__figs { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $rmr-space-md; }
.yt-ds__k { display: flex; align-items: center; gap: 7px; font-size: $rmr-fs-sm; color: $rmr-text-secondary; }
.yt-ds__v { font-size: 24px; font-weight: 800; letter-spacing: -0.02em; color: $rmr-text-primary; margin-top: 3px; }
.yt-ds__p { font-size: $rmr-fs-xs; color: $rmr-text-subtle; }
.yt-key { display: inline-block; width: 10px; height: 10px; border-radius: 3px; }
.yt-sbar { display: flex; gap: 2px; height: 10px; border-radius: 5px; overflow: hidden; span { min-width: 2px; } }

.yt-kpis {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: $rmr-space-sm;
  @media (max-width: 1180px) { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  @media (max-width: 760px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.yt-note { margin: 0; font-size: $rmr-fs-xs; color: $rmr-text-subtle; max-width: 110ch; }
</style>
